import { useState, type Ref } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { useProjects } from '@/hooks/use-projects';
import { Check, ChevronsUpDown, FolderKanban, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ProjectOption {
  id: string;
  name: string;
}

interface ProjectComboboxProps {
  value: ProjectOption | null;
  onChange: (project: ProjectOption | null) => void;
  disabled?: boolean;
  id?: string;
  /** The ✕ "Unlink project" button, for a confirmation to hand focus back to. */
  clearRef?: Ref<HTMLButtonElement>;
  /** The button that opens the list: where focus goes once the ✕ is gone. */
  triggerRef?: Ref<HTMLButtonElement>;
}

/**
 * Picks the one project a board belongs to, or none. It offers the projects
 * the reader may view (`/projects` applies their project-view scope), searched
 * on the server and capped at 50 matches per status, so typing narrows it to
 * any of them. Active and on-hold projects are offered, archived ones are not.
 * A board already linked to an archived project still shows that project's
 * name, since the name comes from `value`, not from the list.
 */
export function ProjectCombobox({ value, onChange, disabled = false, id, clearRef, triggerRef }: ProjectComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // `/projects` filters by one status at a time, so the two are asked
  // separately — and only once the list is opened, since a disabled picker
  // shows `value` alone.
  const query = { search: debouncedSearch || undefined, limit: 50, sort: 'name', order: 'asc' };
  const active = useProjects({ ...query, status: 'active' }, { enabled: open });
  const onHold = useProjects({ ...query, status: 'on_hold' }, { enabled: open });
  const isLoading = active.isLoading || onHold.isLoading;
  // Either half missing would pass for a short list, so either is an error.
  const isError = active.isError || onHold.isError;
  const projects = [...(active.data?.data ?? []), ...(onHold.data?.data ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const select = (project: ProjectOption | null) => {
    onChange(project);
    setSearch('');
    setOpen(false);
  };

  return (
    // `modal`: inside a Dialog the popover's list does not scroll otherwise.
    <Popover modal open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-1.5">
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="flex-1 justify-between h-9 font-normal cursor-pointer min-w-0"
            disabled={disabled}
          >
            {value ? (
              <span className="flex items-center gap-2 min-w-0">
                <FolderKanban className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{value.name}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">No project</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {value && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 cursor-pointer"
            ref={clearRef}
            onClick={() => select(null)}
            aria-label="Unlink project"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search projects..." value={search} onValueChange={setSearch} />
          <CommandList className="max-h-56">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : isError ? (
              <CommandEmpty className="py-4 text-sm text-muted-foreground text-center">
                Projects could not be loaded.
              </CommandEmpty>
            ) : projects.length === 0 ? (
              <CommandEmpty className="py-4 text-sm text-muted-foreground text-center">
                No projects found.
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {projects.map((project) => (
                  <CommandItem
                    key={project.id}
                    value={project.id}
                    onSelect={() => select({ id: project.id, name: project.name })}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn('mr-2 h-4 w-4 shrink-0', value?.id === project.id ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="truncate">{project.name}</span>
                    {project.status === 'on_hold' && (
                      <span className="ml-auto pl-2 text-[11px] text-muted-foreground">On Hold</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
